import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UtensilsCrossed, ClipboardList, Store, Loader2, LogOut, CreditCard, Gift, CheckCircle2, AlertTriangle, BarChart3, Sparkles, HandHelping, XCircle, ExternalLink, FileText, PenLine, KeyRound, Mail } from "lucide-react";
import { SignContract } from "../components/SignContract";
import { API, openPdf } from "../lib/api";
import OrderAlert from "../components/OrderAlert";
import PrinterAlert from "../components/PrinterAlert";
import { toast } from "sonner";
import api from "../lib/api";
import MenuTab from "./restaurateur/MenuTab";
import ProfileTab from "./restaurateur/ProfileTab";
import StatsTab from "./restaurateur/StatsTab";
import PublishBanner from "../components/PublishBanner";
import PublishGuide from "../components/PublishGuide";
import DomainSwitcher from "../components/DomainSwitcher";
import RestaurateurStripeConnect from "../components/RestaurateurStripeConnect";
import RestaurateurEarnings from "../components/RestaurateurEarnings";
import KeepScreenOnToggle from "../components/KeepScreenOnToggle";

// Dashboard restaurateur Nold Go — 3 onglets : Commandes / Ma Carte / Profil.
// Abonnement mensuel supprimé — seule la gestion de menu assistée reste (paiement unique 195€).
export default function RestaurateurDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [billing, setBilling] = useState(null);
  const [assisted, setAssisted] = useState(null);
  const [reliability, setReliability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [assistedLoading, setAssistedLoading] = useState(false);
  const [tab, setTab] = useState(params.get("tab") || "orders");
  const [firstSignupOpen, setFirstSignupOpen] = useState(false); // modal 1ère connexion (comptes créés par admin)

  const load = async () => {
    try {
      const { data: meData } = await api.get("/restaurateur/me");
      setMe(meData);
      if (meData.status !== "approved") { navigate("/restaurateur/inscription", { replace: true }); return; }
      const [ord, bill, ass, rel] = await Promise.all([
        api.get("/restaurateur/orders").catch(() => ({ data: { items: [] } })),
        api.get("/restaurateur/billing/status").catch(() => ({ data: { billing: null } })),
        api.get("/restaurateur/assisted-menu/status").catch(() => ({ data: null })),
        api.get("/restaurateur/reliability").catch(() => ({ data: null })),
      ]);
      setOrders(ord.data.items || []);
      setBilling(bill.data.billing || null);
      setAssisted(ass.data || null);
      setReliability(rel.data || null);
      // Ouvre le modal 1ère connexion uniquement pour comptes créés par l'admin non finalisés
      if (meData.needs_first_signup) setFirstSignupOpen(true);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Retour Stripe Checkout (billing principal OU assisted-menu)
  useEffect(() => {
    const bp = params.get("billing");
    const ap = params.get("assisted");
    if (bp === "success") {
      (async () => {
        try {
          const { data } = await api.post("/restaurateur/billing/verify");
          if (data?.ok) toast.success("Abonnement activé ✅ — prochain prélèvement le " + fmtDate(data.next_charge_at, i18n.language));
          else toast.warning("Paiement en cours de vérification — actualisez la page dans quelques secondes.");
        } catch { toast.error("Impossible de vérifier l'abonnement."); }
        params.delete("billing"); params.delete("session_id"); setParams(params, { replace: true });
        load();
      })();
    } else if (bp === "canceled") {
      toast.info("Abonnement non activé — vous pouvez réessayer à tout moment.");
      params.delete("billing"); setParams(params, { replace: true });
    }
    if (ap === "success") {
      (async () => {
        try {
          const { data } = await api.post("/restaurateur/assisted-menu/verify");
          if (data?.ok) toast.success("Gestion assistée activée ✅ — envoyez vos plats à Nold Go");
          else toast.warning("Paiement en cours de vérification.");
        } catch { toast.error("Impossible de vérifier la gestion assistée."); }
        params.delete("assisted"); params.delete("session_id"); setParams(params, { replace: true });
        load();
      })();
    } else if (ap === "canceled") {
      toast.info("Gestion assistée non activée — vous pouvez réessayer.");
      params.delete("assisted"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Redirection Stripe manuelle uniquement : le restaurateur voit son dashboard et clique sur
  // "Payer 195 € et activer mon compte" quand il est prêt. AUCUN auto-redirect.
  // Nettoie l'ancien flag sessionStorage pour éviter tout side-effect résiduel côté nav.
  useEffect(() => {
    try { sessionStorage.removeItem("nold_pay_triggered"); } catch { /* ignore */ }
  }, []);

  const activateBilling = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/restaurateur/billing/checkout");
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("URL Stripe indisponible.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création du paiement.");
    } finally { setPayLoading(false); }
  };

  const activateAssisted = async () => {
    setAssistedLoading(true);
    try {
      const { data } = await api.post("/restaurateur/assisted-menu/checkout");
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else toast.error("URL Stripe indisponible.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'activation.");
    } finally { setAssistedLoading(false); }
  };

  const cancelAssisted = async () => {
    if (!window.confirm("Repasser en gestion autonome ? L'accès à la gestion assistée restera actif jusqu'à la fin de la période payée.")) return;
    try {
      await api.post("/restaurateur/assisted-menu/cancel");
      toast.success("Passage en gestion autonome enregistré.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  const switchTab = (id) => { setTab(id); params.set("tab", id); setParams(params, { replace: true }); };
  const toggleLang = () => i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0]"><Loader2 className="w-8 h-8 animate-spin text-[#F26419]" /></div>;
  if (!me || me.status !== "approved") return null;

  const resto = me.restaurant || {};
  const bStatus = billing?.status || "pending_setup";
  const isSetupNeeded = ["pending_setup", "past_due", "canceled", "incomplete"].includes(bStatus);
  const isFree = !!billing?.first_month_free;

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="restaurateur-dashboard">
      <DomainSwitcher />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-6 gap-3">
          <div className="min-w-0">
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Nold Go · {t("restaurateur.tagline")}</div>
            <h1 className="mt-1 font-head font-black text-3xl sm:text-4xl text-[#1A1A1A] truncate">{resto.name || t("restaurateur.title")}</h1>
            <p className="text-sm text-[#75807A] mt-1 truncate">{resto.address || ""} {resto.city ? "· " + resto.city : ""}</p>
            {reliability && reliability.score !== null && (
              <div className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-black ${
                reliability.level === "gold" ? "bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]/40" :
                reliability.level === "silver" ? "bg-[#F1F5F9] text-[#475569] border border-[#94A3B8]/40" :
                reliability.level === "bronze" ? "bg-[#FEF3E2] text-[#9A3412] border border-[#F97316]/40" :
                "bg-[#FEE2E2] text-[#991B1B] border border-[#DC2626]/40"
              }`} data-testid="rt-reliability-badge" title={`${reliability.accepted} acceptées / ${reliability.refused} refusées`}>
                {reliability.badge} Fiabilité : {reliability.score}% · {reliability.label}
              </div>
            )}
            {reliability && reliability.level === "new" && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold bg-[#F0FDF4] text-[#166534] border border-[#16a34a]/30" data-testid="rt-reliability-new">
                ✨ {reliability.label}
              </div>
            )}
            {/* Bouton "Voir ma vitrine publique" — s'affiche uniquement si le resto est publié */}
            {me?.publish_gate?.published && me?.restaurant?.id && (
              <a
                href={`/restaurants/${me.restaurant.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rt-preview-storefront"
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#059669] hover:bg-[#047857] text-white text-sm font-bold shadow-md transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Voir ma vitrine publique
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <KeepScreenOnToggle color="green" compact />
            <button onClick={toggleLang} data-testid="rt-lang-toggle" title="Language / Langue"
              className="h-9 px-3 rounded-full bg-white border border-[#E2DFD8] text-xs font-bold text-[#1A1A1A] hover:border-[#F26419]">
              {i18n.language === "fr" ? "🇬🇧 EN" : "🇫🇷 FR"}
            </button>
            <button onClick={() => navigate("/")} className="text-[#75807A] hover:text-[#1A1A1A] text-sm font-medium inline-flex items-center gap-1" data-testid="rt-back-home"><LogOut className="w-4 h-4" /> {t("common.back")}</button>
          </div>
        </div>

        {/* Bandeau d'abonnement mensuel 39 € RETIRÉ — Nold Go est en paiement unique 195 € (activation + pack matériel). */}

        {/* Guide « 3 étapes pour publier » — visible pour les nouveaux inscrits non publiés */}
        {me?.status === "approved" && me?.publish_gate && !me.publish_gate.published && (
          <PublishGuide
            gate={me.publish_gate}
            onSwitchTab={switchTab}
            onPublish={async () => {
              try {
                const { data } = await api.post("/restaurateur/publish");
                toast.success("🎉 Votre vitrine est en ligne sur Nold Go !");
                // Optimistic update — bascule immédiate même si le refetch est lent
                if (data?.restaurant) {
                  setMe((m) => ({
                    ...m,
                    restaurant: data.restaurant,
                    publish_gate: { ...(m?.publish_gate || {}), published: !!data.restaurant.published },
                  }));
                }
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Publication impossible.");
              }
            }}
          />
        )}

        {/* Bandeau Publication Vitrine — le restaurateur publie/dépublie lui-même sur Nold Go */}
        {me?.status === "approved" && me?.publish_gate && (
          <PublishBanner gate={me.publish_gate} onChanged={(fresh) => {
            // Optimistic update immédiat pour éliminer toute latence perceptible :
            // le badge vert "En ligne" s'affiche AVANT que le refetch /me n'arrive.
            if (fresh?.restaurant) {
              setMe((m) => ({
                ...m,
                restaurant: fresh.restaurant,
                publish_gate: {
                  ...(m?.publish_gate || {}),
                  published: !!fresh.restaurant.published,
                },
              }));
            }
            load();
          }} />
        )}

        {/* Stripe Connect Express — reversement automatique 75% à chaque commande livrée */}
        {me?.status === "approved" && (
          <div className="mt-5">
            <RestaurateurStripeConnect />
          </div>
        )}

        {/* Historique des virements Stripe (uniquement pour restos connectés + payouts actifs) */}
        {me?.status === "approved" && (
          <div className="mt-5">
            <RestaurateurEarnings />
          </div>
        )}

        {/* Activation + Pack matériel — paiement unique 195 € */}
        <AssistedMenuSection assisted={assisted} assistedLoading={assistedLoading}
          onActivate={activateAssisted} onCancel={cancelAssisted} onRequestSent={load} />

        {/* Bandeau imprimante Bluetooth non connectée — cliquer relance l'appairage */}
        {me?.status === "approved" && <PrinterAlert />}

        {/* Alerte commande temps réel + impression Bluetooth (visible dès que le compte est validé) */}
        {me?.status === "approved" && (
          <OrderAlert restaurantName={me?.restaurateur?.business_name} />
        )}

        {/* Onglets */}
        <div className="mt-8 border-b border-[#E2DFD8]">
          <nav className="flex gap-1 overflow-x-auto" role="tablist">
            {[
              { id: "orders", label: t("restaurateur.tabs.orders"), icon: ClipboardList, count: orders.length },
              { id: "menu", label: t("restaurateur.tabs.menu"), icon: UtensilsCrossed },
              { id: "lost", label: "Perdues", icon: XCircle },
              { id: "profile", label: t("restaurateur.tabs.profile"), icon: Store },
              { id: "contract", label: "Contrat", icon: FileText },
              { id: "stats", label: t("restaurateur.tabs.stats"), icon: BarChart3 },
            ].map((s) => (
              <button key={s.id} onClick={() => switchTab(s.id)} data-testid={`rt-tab-${s.id}`}
                className={`inline-flex items-center gap-2 h-11 px-4 -mb-px border-b-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                  tab === s.id ? "border-[#F26419] text-[#F26419]" : "border-transparent text-[#75807A] hover:text-[#1A1A1A]"
                }`}
                role="tab" aria-selected={tab === s.id}
              >
                <s.icon className="w-4 h-4" /> {s.label}
                {typeof s.count === "number" && s.count > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F26419] text-white text-[11px] font-bold">{s.count}</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-6">
          {tab === "orders" && <OrdersTab orders={orders} setOrders={setOrders} />}
          {tab === "menu" && <MenuTab />}
          {tab === "lost" && <LostOrdersTab />}
          {tab === "profile" && <ProfileTab resto={resto} onRestoUpdated={(r) => setMe((m) => ({ ...m, restaurant: { ...(m.restaurant || {}), ...r } }))} />}
          {tab === "contract" && <ContractTab />}
          {tab === "stats" && <StatsTab />}
        </div>      </div>

      {/* Modal 1ère connexion — comptes créés par l'admin uniquement, étape optionnelle */}
      {firstSignupOpen && (
        <FirstSignupModal
          currentEmail={me?.current_email || ""}
          isDefaultEmail={!!me?.is_default_email}
          onDone={async () => { setFirstSignupOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

// Modal de bienvenue pour comptes restaurateur créés par l'admin.
// Étape OPTIONNELLE : ils peuvent passer et garder les identifiants existants.
function FirstSignupModal({ currentEmail, isDefaultEmail, onDone }) {
  const [newEmail, setNewEmail] = useState("");
  const [newPin, setNewPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const validate = () => {
    const e = newEmail.trim().toLowerCase();
    const p = newPin.trim();
    if (e && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return "Format d'email invalide.";
    if (p && !/^\d{4,6}$/.test(p)) return "Le code PIN doit contenir 4 à 6 chiffres.";
    if (p && p !== pinConfirm.trim()) return "Les deux codes PIN ne correspondent pas.";
    if (!e && !p) return "Renseignez au moins un email OU un code PIN, ou cliquez sur « Passer »";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await api.post("/restaurateur/me/complete-first-signup", {
        new_email: newEmail.trim() || null,
        new_pin: newPin.trim() || null,
      });
      toast.success("✅ Identifiants mis à jour");
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mise à jour impossible.");
    } finally { setBusy(false); }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await api.post("/restaurateur/me/skip-first-signup");
      toast.info("Étape passée — vos identifiants restent inchangés.");
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="rt-first-signup-modal" className="fixed inset-0 z-[4000] bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 my-8">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] text-white flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-head font-black text-lg text-[#1A1A1A]">Bienvenue chez Nold Go 🎉</h3>
            <p className="text-sm text-[#75807A] mt-1">Votre compte a été créé par notre équipe. Vous pouvez personnaliser vos identifiants ci-dessous ou passer cette étape.</p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl bg-[#FAF5EC] border border-[#E2DFD8] p-3">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#75807A]">Identifiant actuel</div>
          <div className="mt-0.5 text-sm font-mono text-[#1A1A1A] truncate">{currentEmail}</div>
          {isDefaultEmail && <div className="mt-1 text-[11px] text-[#B85014]">⚠️ Adresse par défaut de la plateforme — remplacez-la par votre propre email si possible.</div>}
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-bold text-[#3B423E] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Nouvel email (optionnel)</label>
            <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="votre@email.com" data-testid="rt-fs-email" autoComplete="off"
              className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#3B423E] flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Code PIN (4 à 6 chiffres) — optionnel</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="1234" data-testid="rt-fs-pin" inputMode="numeric" autoComplete="new-password"
                className="h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm text-center tracking-widest font-mono" />
              <input value={pinConfirm} onChange={(e) => setPinConfirm(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="Confirmer" data-testid="rt-fs-pin-confirm" inputMode="numeric" autoComplete="new-password"
                className="h-11 px-3 rounded-xl border-2 border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm text-center tracking-widest font-mono" />
            </div>
            <div className="mt-1 text-[10px] text-[#75807A]">Le PIN servira à vous reconnecter rapidement.</div>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <button data-testid="rt-fs-submit" onClick={submit} disabled={busy}
            className="h-11 rounded-xl bg-gradient-to-r from-[#F26419] to-[#D94F2B] hover:opacity-90 text-white font-bold text-sm disabled:opacity-60">
            {busy ? "Enregistrement…" : "Enregistrer mes identifiants"}
          </button>
          <button data-testid="rt-fs-skip" onClick={skip} disabled={busy}
            className="h-11 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A] hover:text-[#1A1A1A] font-bold text-sm disabled:opacity-60">
            Passer cette étape
          </button>
        </div>
      </div>
    </div>
  );
}

function ContractTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/restaurateur/contract/me").then((r) => setStatus(r.data)).catch(() => setStatus({ signed: false }))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="rounded-3xl bg-white border border-[#E2DFD8] p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#F26419] mx-auto" /></div>;

  const openSigned = () => {
    try {
      openPdf(`${API}/restaurateur/contract/me.pdf`, "mon-contrat-nold-go.pdf");
    } catch { toast.error("Impossible d'ouvrir le PDF."); }
  };

  return (
    <div data-testid="restaurateur-contract-tab">
      {status?.signed ? (
        <div className="rounded-3xl bg-[#F0FDF4] border-2 border-[#16a34a]/40 p-6" data-testid="rt-contract-signed">
          <div className="flex items-start gap-3">
            <div className="text-3xl">✅</div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-black text-[#16a34a]">Contrat signé</div>
              <h2 className="font-head font-black text-2xl mt-1">Votre contrat de partenariat est en règle</h2>
              <p className="text-sm text-[#3B423E] mt-2">
                Signé le <b>{new Date(status.contract?.signed_at).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</b><br/>
                Empreinte : <span className="font-mono text-xs">{status.contract?.hash_short}</span>
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={openSigned} data-testid="rt-contract-download"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm shadow-md">
              <FileText className="w-4 h-4" /> Voir mon contrat signé
            </button>
            {status.contract?.verify_url && (
              <a href={status.contract.verify_url} target="_blank" rel="noopener noreferrer"
                data-testid="rt-contract-verify"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border border-[#E2DFD8] hover:border-[#059669] font-bold text-sm text-[#1A1A1A]">
                <ExternalLink className="w-4 h-4" /> Page de vérification publique
              </a>
            )}
          </div>
        </div>
      ) : (
        <div data-testid="rt-contract-tosign">
          <div className="rounded-3xl bg-[#FFF7ED] border-2 border-[#F26419]/40 p-5 mb-4">
            <div className="flex items-start gap-3">
              <PenLine className="w-5 h-5 text-[#F26419] shrink-0 mt-0.5" />
              <div>
                <div className="font-head font-black text-lg">Signez votre contrat de partenariat</div>
                <p className="text-sm text-[#3B423E] mt-1">Dessinez votre signature au doigt / stylet ci-dessous, puis validez avec le code SMS. Vous recevrez immédiatement une copie PDF signée par email.</p>
              </div>
            </div>
          </div>
          <SignContract
            basePath="/restaurateur/contract"
            docPdfUrl={`${API}/restaurateur/contract/me.pdf`}
            title="Signer mon contrat de partenariat Nold Go"
            subtitle="Lisez le contrat, signez au doigt ou au stylet, validez avec le code SMS."
            signedTitle="Contrat de partenariat signé"
            signedFilename="mon-contrat-nold-go.pdf"
            readLabel="Lire le contrat (PDF)"
            onSigned={async () => {
              // Rafraîchit uniquement le statut du contrat (pas de reload complet),
              // pour éviter de casser la session ou de déconnecter l'imprimante Bluetooth.
              try {
                const r = await api.get("/restaurateur/contract/me");
                setStatus(r.data);
              } catch { setStatus({ signed: true }); }
            }}
          />
        </div>
      )}
    </div>
  );
}

function LostOrdersTab() {
  const [data, setData] = useState({ items: [], stats: { refused: 0, canceled: 0, top_reasons: [] } });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/restaurateur/orders/lost?limit=100");
        setData(r.data || { items: [], stats: { refused: 0, canceled: 0, top_reasons: [] } });
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="rounded-3xl bg-white border border-[#E2DFD8] p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#F26419] mx-auto" /></div>;

  const { items, stats } = data;
  const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div data-testid="restaurateur-lost-orders">
      {/* KPI récapitulatif */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-2xl bg-white border-2 border-[#DC2626]/20 p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#75807A]">Refusées par vous</div>
          <div className="font-head font-black text-3xl text-[#DC2626] mt-1 tabular-nums" data-testid="rt-lost-refused-count">{stats.refused}</div>
        </div>
        <div className="rounded-2xl bg-white border-2 border-[#F59E0B]/20 p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#75807A]">Annulées</div>
          <div className="font-head font-black text-3xl text-[#F59E0B] mt-1 tabular-nums" data-testid="rt-lost-canceled-count">{stats.canceled}</div>
        </div>
      </div>

      {/* Motifs les plus fréquents */}
      {stats.top_reasons?.length > 0 && (
        <div className="rounded-2xl bg-[#FFF7ED] border border-[#F26419]/30 p-4 mb-4" data-testid="rt-lost-top-reasons">
          <div className="text-xs font-bold uppercase tracking-wider text-[#B85014] mb-2">💡 Motifs les plus fréquents — pistes d'amélioration</div>
          <ul className="space-y-1.5">
            {stats.top_reasons.map((r, i) => (
              <li key={i} className="text-sm text-[#1A1A1A] flex items-start gap-2">
                <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-[#F26419] text-white text-xs font-bold shrink-0">{r.count}</span>
                <span className="italic">« {r.reason} »</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2 className="font-head font-bold text-lg mb-3">Historique des commandes perdues</h2>
      {items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">
          🎉 Aucune commande perdue. Continuez comme ça !
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((o) => {
            const isRefused = o.status === "refused_by_resto";
            const reason = o.resto_refuse_reason || o.canceled_reason || "";
            return (
              <li key={o.id} className={`rounded-2xl bg-white border-l-4 border border-[#E2DFD8] p-4 ${isRefused ? "border-l-[#DC2626]" : "border-l-[#F59E0B]"}`} data-testid={`rt-lost-order-${o.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${isRefused ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                        {isRefused ? "Refusée" : "Annulée"}
                      </span>
                      <span className="text-xs text-[#75807A]">#{(o.id || "").slice(0, 8).toUpperCase()}</span>
                    </div>
                    {reason && <div className="text-sm text-[#1A1A1A] mt-1.5 italic">« {reason} »</div>}
                    {!reason && <div className="text-xs text-[#75807A] mt-1.5 italic">Aucun motif renseigné</div>}
                    {o.customer_name && <div className="text-xs text-[#3B423E] mt-1">👤 {o.customer_name}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-[#75807A]">{fmtDate(o.resto_refused_at || o.canceled_at || o.created_at)}</div>
                    {typeof o.total_eur !== "undefined" && <div className="text-sm font-bold text-[#3B423E] mt-1">{fmtEur(o.total_eur)}</div>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function OrdersTab({ orders, setOrders }) {
  const { t } = useTranslation();
  const [busyReady, setBusyReady] = useState(null);
  const [tick, setTick] = useState(0);
  // Retick chaque minute pour rafraîchir les timers cuisine sans re-fetch.
  useEffect(() => { const id = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(id); }, []);
  const markReady = async (orderId) => {
    setBusyReady(orderId);
    try {
      const { data } = await api.post(`/restaurateur/orders/${orderId}/ready`);
      toast.success("🍽️ Livreur notifié — commande prête ✅");
      // Mise à jour immutable de l'état parent — React détecte proprement le changement
      // et re-render sans polluer le tableau partagé avec les autres composants (ex. sirène).
      setOrders((prev) => prev.map((o) =>
        o.id === orderId
          ? { ...o, status: data.status || "ready_for_pickup", resto_ready_at: data.resto_ready_at || o.resto_ready_at }
          : o
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusyReady(null); }
  };
  // Timer cuisine : calcule le nombre de minutes depuis l'acceptation.
  // Coloration : ≤15min vert · 15-25 orange · >25 rouge (retard visible).
  const cookingTimer = (o) => {
    const iso = o.resto_accepted_at;
    if (!iso || o.status === "ready_for_pickup" || ["delivered","completed","picked_up","out_for_delivery"].includes(o.status)) return null;
    const start = new Date(iso).getTime();
    if (!start) return null;
    const mins = Math.floor((Date.now() - start) / 60000);
    if (mins < 0) return null;
    const color = mins <= 15 ? "bg-[#F0FDF4] text-[#166534] border-[#16a34a]/30" :
                  mins <= 25 ? "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/40" :
                              "bg-[#FEE2E2] text-[#991B1B] border-[#DC2626]/40";
    return { mins, color };
  };
  // Total du jour : montant + nombre de commandes reçues aujourd'hui.
  // Filtre local (date navigateur) pour rester en phase avec ce que voit le restaurateur.
  const todayStr = new Date().toISOString().slice(0, 10);
  const today = orders.filter((o) => {
    const iso = o.paid_at || o.created_at || "";
    return iso.slice(0, 10) === todayStr;
  });
  const totalTodayEur = today.reduce((s, o) => s + (Number(o.total_eur) || 0), 0);
  const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  // Un plat unique = objet items[]. Fallback vers `dishes` / `item_description` pour ancien schéma.
  const summarize = (o) => {
    if (Array.isArray(o.items) && o.items.length) {
      // Un item par ligne + le name (avec options intégrées via \n) reste tel quel.
      // Le conteneur a whitespace-pre-line → les sauts de ligne du name (options) sont préservés.
      return o.items.map((it) => `${it.qty || it.quantity || 1}× ${it.name}`).join("\n");
    }
    return o.dishes || o.item_description || "";
  };
  const routeLine = (o) => {
    // Food orders : delivery_address. Delivery legacy : pickup → destination.
    if (o.delivery_address) return `→ ${o.delivery_address}`;
    if (o.pickup || o.destination) return `${o.pickup || "—"} → ${o.destination || "—"}`;
    return "";
  };
  return (
    <div data-testid="restaurateur-orders">
      {/* KPI "Aujourd'hui" — nombre de commandes + montant total en € */}
      <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] text-white p-4 flex items-center justify-between gap-3" data-testid="rt-orders-today-kpi">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-90">Aujourd'hui</div>
          <div className="font-head font-black text-3xl leading-none mt-1 tabular-nums" data-testid="rt-orders-today-total">{fmtEur(totalTodayEur)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-90">Commandes</div>
          <div className="font-head font-black text-3xl leading-none mt-1 tabular-nums" data-testid="rt-orders-today-count">{today.length}</div>
        </div>
      </div>
      <h2 className="font-head font-bold text-lg mb-3">{t("restaurateur.orders.title")}</h2>
      {orders.length === 0 ? (
        <div className="rounded-3xl bg-white border border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">{t("restaurateur.orders.empty")}</div>
      ) : (
        <ul className="space-y-3">
          {orders.map((o) => (
            <li key={o.id} className="rounded-2xl bg-white border border-[#E2DFD8] p-4" data-testid={`rt-order-${o.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[#1A1A1A] truncate">🍔 {o.restaurant_name || "—"}</div>
                  <div className="text-xs text-[#75807A] whitespace-pre-line mt-0.5">{summarize(o)}</div>
                  <div className="text-xs text-[#3B423E] mt-1">{routeLine(o)}</div>
                  {o.customer_name && <div className="text-xs text-[#3B423E] mt-0.5">👤 {o.customer_name}{o.customer_phone ? ` · ${o.customer_phone}` : ""}</div>}
                  {typeof o.total_eur !== "undefined" && <div className="text-sm font-semibold text-[#16a34a] mt-1">{Number(o.total_eur).toFixed(2)} €</div>}
                  {typeof o.commission_rate_pct !== "undefined" && (
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#F0FDF4] border border-[#16a34a]/30 px-2 py-0.5 text-[11px] font-bold text-[#16a34a]" title="Commission Nold Go sur cette commande">
                      Commission Nold Go : {Number(o.commission_rate_pct).toFixed(0)} %
                      {typeof o.commission_eur !== "undefined" && ` (${Number(o.commission_eur).toFixed(2)} €)`}
                      {typeof o.resto_payout_eur !== "undefined" && ` · Vous : ${Number(o.resto_payout_eur).toFixed(2)} €`}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-[#75807A]">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="mt-1 inline-block rounded-full bg-[#F0EBE1] px-2 py-0.5 text-[11px] font-semibold text-[#3B423E]">{o.status}</div>
                </div>
              </div>
              {/* Bouton "Commande prête" — visible uniquement pour les commandes acceptées mais pas encore prêtes.
                  Notifie le livreur + le client + met le statut à ready_for_pickup. */}
              {o.resto_status === "accepted" && o.status !== "ready_for_pickup" && !["delivered","completed","canceled","refused_by_resto","picked_up","out_for_delivery"].includes(o.status) && (
                <button
                  onClick={() => markReady(o.id)}
                  disabled={busyReady === o.id}
                  data-testid={`rt-order-ready-${o.id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#16a34a] hover:bg-[#127a37] text-white font-black text-sm disabled:opacity-60 disabled:cursor-not-allowed shadow-md"
                >
                  {busyReady === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {busyReady === o.id ? "Notification livreur…" : "🍽️ Commande prête — appeler le livreur"}
                </button>
              )}
              {o.status === "ready_for_pickup" && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F0FDF4] text-[#166534] px-3 py-1.5 text-xs font-black border border-[#16a34a]/30">
                  ✅ Prête · Livreur notifié
                </div>
              )}
              {/* Timer cuisine — chrono depuis l'acceptation (Feature Timer Cuisine) */}
              {(() => {
                const timer = cookingTimer(o);
                if (!timer) return null;
                return (
                  <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-black border ${timer.color}`}
                       data-testid={`rt-order-cooking-timer-${o.id}`} title="Temps depuis l'acceptation">
                    ⏱️ En préparation depuis {timer.mins} min{timer.mins > 1 ? "s" : ""}
                    {timer.mins > 25 && <span className="ml-1">— retard ⚠️</span>}
                  </div>
                );
              })()}
              {/* Livreur en route — badge visible dès que picked_up (Feature Confirmation Livreur Reçu) */}
              {["picked_up", "out_for_delivery"].includes(o.status) && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#EFF6FF] text-[#1D4ED8] px-3 py-1.5 text-xs font-black border border-[#3B82F6]/30"
                     data-testid={`rt-order-driver-picked-${o.id}`}>
                  🛵 Livreur en route
                  {o.driver_name && <span className="opacity-80 font-semibold">· {o.driver_name}</span>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function fmtDate(iso, lang = "fr") {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "fr-BE", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

function BillingBanner({ billing, isSetupNeeded, isFree, payLoading, onActivate, lang }) {
  const amount = ((billing.amount_cents || 19500) / 100).toFixed(2).replace(".", ",") + " €";
  const nextDate = fmtDate(billing.next_charge_at || billing.current_period_end || billing.trial_end, lang);
  const rank = billing.approval_rank;
  const quota = billing.quota_free || 10;

  if (isSetupNeeded) {
    const isPastDue = billing.status === "past_due";
    return (
      <div className={`rounded-3xl p-6 mt-2 ${isPastDue ? "bg-[#FEE2E2] border-2 border-[#D32F2F]" : "bg-gradient-to-br from-[#F26419] to-[#D94D0E] text-white"}`} data-testid="rt-billing-banner">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 w-12 h-12 rounded-2xl grid place-items-center ${isPastDue ? "bg-white" : "bg-white/20"}`}>
            {isPastDue ? <AlertTriangle className="w-6 h-6 text-[#D32F2F]" /> : <CreditCard className="w-6 h-6 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-head font-black text-lg ${isPastDue ? "text-[#7A1010]" : ""}`}>
              {isPastDue ? "⚠️ Paiement en échec" : "Activation + Pack matériel"}
            </div>
            <div className={`text-sm mt-1 ${isPastDue ? "text-[#7A1010]" : "text-white/90"}`}>
              {isPastDue ? "Votre carte a été refusée. Mettez à jour votre moyen de paiement pour continuer à recevoir des commandes." : (
                <>
                  <b>{amount}</b> · paiement unique via Stripe (activation compte + pack matériel).<br />
                  {isFree ? (
                    <span className="inline-flex items-center gap-1 mt-1 bg-white/25 rounded-full px-2 py-0.5 text-xs font-semibold"><Gift className="w-3 h-3" /> 1<sup>er</sup> mois offert (top {quota}) · 1<sup>er</sup> prélèvement le {nextDate}</span>
                  ) : (
                    <span>1<sup>er</sup> prélèvement le <b>{nextDate}</b>.</span>
                  )}
                </>
              )}
            </div>
            <button onClick={onActivate} disabled={payLoading} data-testid="rt-billing-activate"
              className={`mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full font-bold text-sm transition-colors ${isPastDue ? "bg-[#D32F2F] hover:bg-[#B71C1C] text-white" : "bg-white text-[#F26419] hover:bg-[#FFF3E6]"}`}>
              {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {isPastDue ? "Mettre à jour la carte" : "Activer avec Stripe →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusLabel = billing.status === "trialing" ? "En essai gratuit" : "Actif";
  const statusColor = billing.status === "trialing" ? "text-[#F26419]" : "text-[#16a34a]";
  return (
    <div className="rounded-3xl bg-white border-2 border-[#DCFCE7] p-6 mt-2" data-testid="rt-billing-banner-active">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 rounded-2xl grid place-items-center bg-[#DCFCE7]"><CheckCircle2 className="w-6 h-6 text-[#16a34a]" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-head font-black text-lg text-[#1A1A1A]">Abonnement Nold Go · <span className={statusColor}>{statusLabel}</span></div>
          <div className="text-sm text-[#3B423E] mt-1">
            <b>{amount}</b> · payé une seule fois (activation + pack matériel).
            {isFree && rank && <span className="ml-1 text-[#F26419] font-semibold">🎁 1<sup>er</sup> mois offert</span>}
          </div>
        </div>
      </div>
    </div>
  );
}


function AssistedMenuSection({ assisted, assistedLoading, onActivate, onCancel, onRequestSent }) {
  const [showForm, setShowForm] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const a = assisted || { enabled: false, status: "inactive" };
  const isActive = a.enabled && ["active", "trialing"].includes(a.status);

  // Charge l'historique dès qu'une facture est signalée par le status.
  useEffect(() => {
    if (!a.invoice_id) { setInvoices([]); return; }
    let cancelled = false;
    api.get("/restaurateur/assisted-menu/invoices").then((r) => {
      if (!cancelled) setInvoices(r.data?.invoices || []);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [a.invoice_id]);

  const InvoicesHistory = () => {
    if (!invoices.length) return null;
    return (
      <div className="mt-4 rounded-2xl bg-white border border-[#E2DFD8] p-4" data-testid="assisted-menu-invoices">
        <h4 className="font-head font-black text-sm text-[#1A1A1A] flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-[#F26419]" /> Mes factures Nold Go
        </h4>
        <ul className="mt-3 divide-y divide-[#F0EBE1]">
          {invoices.map((inv) => (
            <li key={inv.id} data-testid={`assisted-menu-invoice-${inv.id}`}
              className="py-2 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm text-[#1A1A1A] truncate">{inv.id}</div>
                <div className="text-xs text-[#75807A]">
                  {new Date(inv.issued_at).toLocaleDateString("fr-BE")} · {(inv.amount_cents / 100).toFixed(2).replace(".", ",")} €
                </div>
              </div>
              <a href={`${API}/api/restaurateur/assisted-menu/invoice.pdf?invoice_id=${encodeURIComponent(inv.id)}`}
                target="_blank" rel="noopener noreferrer"
                data-testid={`assisted-menu-invoice-download-${inv.id}`}
                className="shrink-0 h-9 px-3 rounded-full bg-white border border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] text-xs font-bold inline-flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Télécharger
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!isActive) {
    return (
      <div className="mt-4" data-testid="assisted-menu-choice">
        <div className="rounded-3xl bg-gradient-to-br from-[#F26419] to-[#D94D0E] text-white border-2 border-[#F26419] p-5 relative overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5" />
            <h3 className="font-head font-black text-lg">Activation + Pack matériel</h3>
          </div>
          <p className="text-sm text-white/90">Paiement unique pour activer votre compte partenaire Nold Go et recevoir votre pack matériel (imprimante ticket, autocollants vitrine, flyers).</p>
          <div className="mt-3 text-2xl font-black">195 €<span className="text-sm font-semibold"> · paiement unique</span></div>
          <button data-testid="assisted-menu-subscribe" onClick={onActivate} disabled={assistedLoading}
            className="mt-3 w-full h-11 rounded-xl bg-white text-[#F26419] font-bold hover:bg-white/90 disabled:opacity-60">
            {assistedLoading ? "Redirection Stripe…" : "Payer 195 € et activer mon compte"}
          </button>
        </div>
        <InvoicesHistory />
      </div>
    );
  }

  const lr = a.last_request || null;
  const nb = lr?.dishes?.length || 0;

  return (
    <div className="mt-4 rounded-3xl bg-white border-2 border-[#F26419] p-5" data-testid="assisted-menu-active">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-11 h-11 rounded-2xl grid place-items-center bg-[#F26419]/15">
          <Sparkles className="w-6 h-6 text-[#F26419]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-head font-black text-lg text-[#1A1A1A]">Compte activé + Pack matériel</h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-[#F26419]/15 px-2 py-0.5 text-[11px] font-bold text-[#B85014]">
              <CheckCircle2 className="w-3 h-3" /> Paiement reçu
            </span>
          </div>
          <p className="text-sm text-[#3B423E] mt-1"><b>195 € — paiement unique</b> réglé via Stripe. Retrouvez votre facture ci-dessous.</p>

          {/* Suivi livraison du pack matériel (imprimante + autocollants + flyers). */}
          {(() => {
            const st = a.pack_status || "pending";
            const label = { pending: "En préparation", shipped: "Expédié", delivered: "Livré",
                            not_paid: "En attente de paiement" }[st] || "En préparation";
            const bg = { pending: "bg-[#FFF3E6] text-[#B85014] border-[#F26419]",
                         shipped: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]",
                         delivered: "bg-[#DCFCE7] text-[#166534] border-[#16A34A]",
                         not_paid: "bg-[#F0EBE1] text-[#75807A] border-[#B0AA9E]" }[st];
            return (
              <div className="mt-3 rounded-2xl border p-3 flex items-start gap-3 flex-wrap" data-testid="pack-tracking">
                <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider border ${bg}`}>
                  📦 Pack matériel · {label}
                </div>
                {a.pack_tracking_number && (
                  <div className="text-xs text-[#75807A]">N° suivi : <b className="text-[#1A1A1A]">{a.pack_tracking_number}</b></div>
                )}
                {a.pack_shipped_at && st !== "delivered" && (
                  <div className="text-xs text-[#75807A]">Expédié le {new Date(a.pack_shipped_at).toLocaleDateString("fr-BE")}</div>
                )}
                {a.pack_delivered_at && (
                  <div className="text-xs text-[#166534] font-semibold">Livré le {new Date(a.pack_delivered_at).toLocaleDateString("fr-BE")}</div>
                )}
              </div>
            );
          })()}

          <InvoicesHistory />
          {lr && (
            <div className="mt-3 rounded-2xl bg-[#FFF3E6] border border-[#F26419]/30 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-semibold text-[#1A1A1A]">Dernière demande — {nb} plat{nb > 1 ? "s" : ""}</div>
                <div className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${lr.status === "done" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                  {lr.status === "done" ? "Traitée" : "En attente"}
                </div>
              </div>
              {lr.notes && <div className="text-xs text-[#75807A] mt-1">« {lr.notes} »</div>}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="assisted-menu-send" onClick={() => setShowForm(true)}
              className="h-10 px-4 rounded-xl bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm inline-flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Envoyer mes plats à Nold Go
            </button>
            <button data-testid="assisted-menu-cancel" onClick={onCancel}
              className="h-10 px-4 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#DC2626] hover:text-[#DC2626] text-[#75807A] font-bold text-sm">
              Repasser en autonomie
            </button>
          </div>
        </div>
      </div>
      {showForm && <AssistedRequestForm onClose={() => setShowForm(false)} onSent={() => { setShowForm(false); onRequestSent(); }} />}
    </div>
  );
}


function AssistedRequestForm({ onClose, onSent }) {
  const [notes, setNotes] = useState("");
  const [dishes, setDishes] = useState([{ name: "", price_eur: "", description: "", category: "", photo_data_url: null }]);
  const [submitting, setSubmitting] = useState(false);

  const addRow = () => setDishes((d) => [...d, { name: "", price_eur: "", description: "", category: "", photo_data_url: null }]);
  const removeRow = (i) => setDishes((d) => d.filter((_, k) => k !== i));
  const update = (i, k, v) => setDishes((d) => d.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const onFile = (i, file) => {
    if (!file) { update(i, "photo_data_url", null); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo trop lourde (max 8 Mo)."); return; }
    const r = new FileReader();
    r.onload = () => update(i, "photo_data_url", r.result);
    r.readAsDataURL(file);
  };

  const submit = async () => {
    const clean = dishes.filter((d) => (d.name || "").trim().length >= 2);
    if (clean.length === 0) { toast.error("Ajoutez au moins un plat avec un nom."); return; }
    setSubmitting(true);
    try {
      await api.post("/restaurateur/assisted-menu/request", {
        notes: notes.trim(),
        dishes: clean.map((d) => ({
          name: d.name.trim(),
          price_eur: d.price_eur === "" ? null : parseFloat(d.price_eur),
          description: (d.description || "").trim(),
          category: (d.category || "").trim(),
          photo_data_url: d.photo_data_url || null,
        })),
      });
      toast.success(`✅ Demande envoyée à Nold Go (${clean.length} plat(s)) — notre équipe s'en occupe.`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="assisted-request-form">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">Envoyer mes plats à Nold Go</h3>
            <p className="text-xs text-[#75807A] mt-1">Nom, prix, description (facultatif) et photo (facultatif). Notre équipe s'occupe du reste.</p>
          </div>
          <button onClick={onClose} className="text-[#75807A] hover:text-[#1A1A1A] text-sm font-bold">✕</button>
        </div>

        <textarea data-testid="assisted-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes pour Nold Go (ex : mettre en ligne pour vendredi, cuisine italienne, allergènes…)"
          className="w-full min-h-[70px] rounded-xl border border-[#E2DFD8] p-3 text-sm mb-3" />

        <div className="space-y-3">
          {dishes.map((d, i) => (
            <div key={i} className="rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-3" data-testid={`assisted-dish-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input data-testid={`dish-name-${i}`} value={d.name} onChange={(e) => update(i, "name", e.target.value)}
                  placeholder="Nom du plat*" className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-sm sm:col-span-2" />
                <input data-testid={`dish-price-${i}`} type="number" step="0.01" min="0" value={d.price_eur} onChange={(e) => update(i, "price_eur", e.target.value)}
                  placeholder="Prix €" className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={d.category} onChange={(e) => update(i, "category", e.target.value)}
                  placeholder="Catégorie (entrées, plats, desserts…)" className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-sm" />
                <label className="h-10 rounded-lg border border-dashed border-[#E2DFD8] px-3 text-sm text-[#75807A] hover:border-[#F26419] cursor-pointer inline-flex items-center gap-2">
                  📷 {d.photo_data_url ? "Photo ✓" : "Ajouter photo (facultatif)"}
                  <input type="file" accept="image/*" onChange={(e) => onFile(i, e.target.files?.[0])} className="hidden" />
                </label>
              </div>
              <textarea value={d.description} onChange={(e) => update(i, "description", e.target.value)}
                placeholder="Description (ingrédients, allergènes)"
                className="mt-2 w-full min-h-[50px] rounded-lg border border-[#E2DFD8] p-2 text-sm" />
              {dishes.length > 1 && (
                <button onClick={() => removeRow(i)} className="mt-1 text-xs text-[#DC2626] hover:underline">Supprimer ce plat</button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-3 flex gap-2 flex-wrap justify-between">
          <button data-testid="add-dish" onClick={addRow} className="h-10 px-4 rounded-xl bg-white border-2 border-[#F26419] text-[#F26419] font-bold text-sm">+ Ajouter un plat</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A] font-bold text-sm">Annuler</button>
            <button data-testid="submit-assisted-request" onClick={submit} disabled={submitting}
              className="h-10 px-5 rounded-xl bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm disabled:opacity-60">
              {submitting ? "Envoi…" : "Envoyer à Nold Go"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
